import { useCallback, useEffect, useState } from 'react';
import env from '@beam-australia/react-env';
import { shortenId } from '@beamible/uuid';
import { useAuth } from '@frontegg/nextjs';
import useClient from '@pkg/client/useClient';
import useDatabase from '@pkg/database/useDatabase';
import useLibrary from '@pkg/entities/library/useLibrary';
import deriveOrgProps from '@pkg/entities/organisations/deriveProps';

const GET_PROFILE = /* GraphQL */ `
  query GetProfile {
    me {
      id
      first_name
      last_name
      email
      avatar
      is_owner
      is_role_only
      intercom_identity
      person: active_person {
        uuid
      }
      created_at
      updated_at
      is_registered
      access {
        permissions {
          design
          organisation
        }
        roles {
          design
          organisation
        }
      }
      features
    }
    organisation: me {
      data: active_organisation {
        uuid
        name
        tenant_id
        is_disabled
        is_restricted
        owner {
          id
          first_name
          last_name
          email
          avatar
        }
        integrations {
          uuid
          name
          type
          status
          latest_import {
            created_at
          }
          image_url
          created_at
        }
      }
    }
  }
`;

export default function useRefresh() {
  const client = useClient();
  const database = useDatabase();
  const library = useLibrary();
  const { user } = useAuth();
  const [isRefreshing, setRefreshing] = useState(false);

  const refresh = useCallback(() => {
    if (!isRefreshing) {
      setRefreshing(true);
    }
  }, []);

  const handleRefresh = async () => {
    // the account's design is a design rather than a property of the account,
    // so it is read from the design surface and carried on the organisation
    // where the app already looks for it
    const [{ data }, current, designs] = await Promise.all([
      await client.graphql({ query: GET_PROFILE }),
      await database.auth.get('me'),
      // a profile is still a profile without it, and a limited session may not
      // be allowed to list designs at all
      await client.get('v1/designs?owner_type=ACCOUNT').catch(() => null),
    ]);

    // api returned a different user to the user in indexedDB
    if (current?.id && data?.me?.id && data?.me?.id !== current?.id) {
      try {
        await database.reset();
      } catch (error) {
        console.error('Error resetting database: ', error);
        await database.hash.clear();
      }
    }

    // api returned no user
    // user exists in frontegg but not Beamible or JWT expired
    /** @todo throw an error that is handled by an error boundary */
    if (!data?.me) {
      await database.transaction('rw', ['hash', 'auth'], () => {
        database.hash.put({ key: 'auth', updated_at: Date.now() });
        database.auth.put({ key: 'organisation' });
        return database.auth.put({
          key: 'me',
          error: 'Unable to retrieve user details from Beamible',
        });
      });
      setRefreshing(false);
      return;
    }

    // Avoid loading a different organisation to the Frontegg JWT tenant
    const activeTenantId = user?.tenantId;
    const orgTenantId = data.organisation?.data?.tenant_id;
    if (activeTenantId && orgTenantId && orgTenantId !== activeTenantId) {
      setRefreshing(false);
      return;
    }

    const [account] = designs?.data ?? [];
    const organisation = deriveOrgProps(
      library,
      data.organisation?.data && {
        ...data.organisation.data,
        design: account ? { uuid: shortenId(account.id) } : null,
      }
    );
    await database.transaction('rw', ['hash', 'build', 'auth'], () => {
      const buildId = env('PLATFORM_VER') ? env('PLATFORM_VER') : 'development';
      database.build.put({
        key: 'build',
        id: buildId,
      });
      database.hash.put({ key: 'auth', updated_at: Date.now() });
      return database.auth.bulkPut([
        { key: 'me', ...data.me },
        { key: 'organisation', ...organisation },
      ]);
    });

    setRefreshing(false);
  };

  useEffect(() => {
    if (isRefreshing) {
      handleRefresh();
    }
  }, [isRefreshing]);

  return {
    refresh,
    handleRefresh,
    isRefreshing,
  };
}
